/**
 * CardEffectAckModal.tsx — R3 Bug 6
 *
 * Shown after a card effect resolves (event/policy/mitigate).
 * Displays all non-zero numeric deltas (Energy / Tokens / Pollution / Reputation).
 * Blocks turn flow until the player clicks Acknowledge.
 *
 * Spec constraints:
 *   - NO backdrop close (click-outside does nothing)
 *   - NO Esc close
 *   - Single "Acknowledge" button
 *   - Native <dialog open> per spec
 *
 * VISIBLE WHEN: pendingEffectAck !== null (set by playCard in game-store).
 */

import type React from "react";
import { useEffect, useRef } from "react";
import { useGameStore } from "../../store/game-store.js";
import styles from "./CardEffectAckModal.module.css";

type DeltaRow = {
  label: string;
  value: number;
};

function buildRows(deltas: {
  energy: number;
  tokens: number;
  pollution: number;
  reputation: number;
}): DeltaRow[] {
  const rows: DeltaRow[] = [
    { label: "Tokens", value: deltas.tokens },
    { label: "Energy", value: deltas.energy },
    { label: "Pollution", value: deltas.pollution },
    { label: "Reputation", value: deltas.reputation },
  ];
  // Spec: skip zero deltas
  return rows.filter((r) => r.value !== 0);
}

function formatDelta(value: number, label: string): string {
  const sign = value > 0 ? "+" : "";
  return `${label} ${sign}${value}`;
}

function deltaClass(value: number): string {
  if (value > 0) return styles.deltaPositive ?? "deltaPositive";
  if (value < 0) return styles.deltaNegative ?? "deltaNegative";
  return styles.deltaNeutral ?? "deltaNeutral";
}

export function CardEffectAckModal(): React.ReactElement | null {
  const pendingEffectAck = useGameStore((s) => s.pendingEffectAck);
  const dismissEffectAck = useGameStore((s) => s.dismissEffectAck);
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Spec: NO Esc close — intercept Esc on the dialog element.
  useEffect(() => {
    const el = dialogRef.current;
    if (el === null) return;
    const blockEsc = (e: Event) => {
      e.preventDefault();
    };
    el.addEventListener("cancel", blockEsc);
    return () => el.removeEventListener("cancel", blockEsc);
  }, []);

  if (pendingEffectAck === null) return null;

  const rows = buildRows(pendingEffectAck.deltas);

  return (
    // Spec: NO backdrop close — scrim div does nothing on click
    <div className={styles.scrim} data-testid="effect-ack-scrim">
      <dialog
        ref={dialogRef}
        open
        className={styles.dialog}
        data-testid="effect-ack-dialog"
        aria-labelledby="ack-title"
        aria-modal="true"
      >
        <div className={styles.panel}>
          <div className={styles.title} id="ack-title" data-testid="effect-ack-title">
            {pendingEffectAck.cardName}
          </div>
          <div className={styles.subtitle}>Effect resolved</div>

          {rows.length > 0 ? (
            <ul className={styles.deltaList} data-testid="effect-ack-delta-list">
              {rows.map((row) => (
                <li
                  key={row.label}
                  className={[styles.deltaItem, deltaClass(row.value)].join(" ")}
                  data-testid={`delta-${row.label.toLowerCase()}`}
                >
                  {formatDelta(row.value, row.label)}
                </li>
              ))}
            </ul>
          ) : (pendingEffectAck.notes?.length ?? 0) >
            0 ? null : pendingEffectAck.conditionsNotMet === true ? (
            <div className={styles.deltaItem} data-testid="effect-ack-no-change">
              Conditions not met — card effect skipped
            </div>
          ) : (
            <div className={styles.deltaItem} data-testid="effect-ack-no-change">
              No resource change
            </div>
          )}

          {(pendingEffectAck.notes?.length ?? 0) > 0 && (
            <ul className={styles.noteList} data-testid="effect-ack-notes">
              {pendingEffectAck.notes?.map((note) => (
                <li key={note} className={styles.noteItem} data-testid="effect-ack-note">
                  {note}
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            className={styles.ackBtn}
            data-testid="btn-effect-acknowledge"
            onClick={dismissEffectAck}
          >
            ✓ Acknowledge
          </button>
        </div>
      </dialog>
    </div>
  );
}
